import { Node } from "@tiptap/pm/model";
import { NodeViewWrapper } from "@tiptap/react";
import React, { useEffect, useMemo, useRef } from "react";
import { cn } from "../../../lib/utils/index.js";
import VideoPlayer from "./videojs/VideoPlayer.js";
import { useGetAssetsAspectRatio } from "../../serverside/getAssetsAspectRatio.js";

interface VideoBlockViewProps {
  node: Node & {
    attrs: {
      src: string;
      poster: string;
      assetId: string;
      playlistUrl?: string;
    };
  };
  className?: string;
  getVideoAspect?: () => void;
}

export type Playlist = {
  id: string;
  title: string;
  description: string;
  poster: string;
  items: Item[];
};

export type Item = {
  src: string;
  type: string;
  label: string;
  width: number;
  height: number;
};

export const VideoBlockViewClientsideV1 = ({
  className,
  getVideoAspect,
  ...props
}: VideoBlockViewProps) => {
  const { node } = props;
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const { src, poster, playlistUrl, caption, actualWidth, actualHeight } =
    node.attrs;
  const { aspectRatio, calculateAspectRatio, isVertical } =
    useGetAssetsAspectRatio();
  const [playlistContent, setPlaylistContent] = React.useState<
    Playlist | undefined
  >();
  const [error, setError] = React.useState();

  useEffect(() => {
    async function getPlaylistContent() {
      const res = await fetch(playlistUrl, {
        cache: "no-cache",
      });
      return await res.json();
    }

    if (playlistUrl) {
      try {
        getPlaylistContent()
          .then((data) => {
            setPlaylistContent(data);
          })
          .catch((err) => {
            setError(err);
          });
      } catch (error) {
        setError(error);
      }
    }
  }, [playlistUrl]);

  const wrapperClassName = cn(
    node.attrs.align === "left" ? "ml-0" : "ml-auto",
    node.attrs.align === "right" ? "mr-0" : "mr-auto",
    node.attrs.align === "center" && "mx-auto"
  );

  const videoJsOptions = useMemo(() => {
    if (playlistContent == undefined) return {};

    if (
      playlistContent.items.some(
        (item) =>
          item.type === "application/x-mpegURL" ||
          item.type === "application/vnd.apple.mpegurl"
      )
    ) {
      return {
        sources: playlistContent.items
          .filter(
            (item) =>
              item.type === "application/x-mpegURL" ||
              item.type === "application/vnd.apple.mpegurl"
          )
          .map((item) => ({
            src: item.src,
            type: item.type,
            label: item.label,
          })),
        poster: playlistContent.poster,
        preload: "auto",
      };
    }
    return {
      sources: playlistContent.items.map((item) => ({
        src: item.src,
        type: item.type,
        label: item.label,
      })),
      poster: playlistContent.poster,
      preload: "auto",
    };
  }, [playlistContent]);

  useEffect(() => {
    if (!playlistContent?.items?.length) {
      return;
    }
    const source = playlistContent.items.find((item) => item.src);
    if (!aspectRatio && source?.src) {
      calculateAspectRatio(
        {
          src: source.src,
          actualWidth: actualWidth || source?.width,
          actualHeight: actualHeight || source?.height,
        },
        "VIDEO",
        getVideoAspect
      );
    }
  }, [playlistContent]);
  const style = useMemo(() => {
    if (aspectRatio) {
      return {
        aspectRatio,
        width: node.attrs.width,
      };
    }
    return {
      width: node.attrs.width,
    };
  }, [aspectRatio]);
  return (
    <NodeViewWrapper className={className}>
      <div
        className={cn(
          wrapperClassName,
          "main-videojs-wrapper aspect-square h-full"
        )}
        {...(isVertical && { "data-vertical": isVertical })}
        ref={videoWrapperRef}
        style={style}
      >
        {error && <div>{error}</div>}

        {playlistContent && <VideoPlayer options={videoJsOptions} />}
      </div>
      {caption && playlistContent?.title && (
        <div
          className="block mx-auto text-sm text-gray-500 text-center mt-1"
          style={{ width: node.attrs.width }}
        >
          {playlistContent?.title}
        </div>
      )}
    </NodeViewWrapper>
  );
};

export default VideoBlockViewClientsideV1;
